// Platform super-admin session store — DELIBERATELY separate from the tenant session store
// (lib/auth.ts). The two never share a key, so a founder's console session and a customer's tenant
// session can coexist in the same browser without one overwriting or leaking into the other (SAAS-
// ARCHITECTURE §5). The backend enforces the real boundary (a tenant JWT is rejected by
// requirePlatformAdmin and vice-versa); this mirrors that separation on the client.
//
// Keys are namespaced `cc.platform.*` — the tenant store uses `cc.*` (accessToken/refreshToken/user).

export interface PlatformAdmin {
  id: string;
  email: string;
  name: string | null;
}

const ACCESS_KEY = 'cc.platform.accessToken';
const REFRESH_KEY = 'cc.platform.refreshToken';
const ADMIN_KEY = 'cc.platform.admin';

export function getPlatformAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
export function getPlatformRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function getStoredPlatformAdmin(): PlatformAdmin | null {
  const raw = localStorage.getItem(ADMIN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PlatformAdmin;
  } catch {
    return null;
  }
}

/** True when a platform session token is present — the client-side gate for the /admin area. */
export function hasPlatformSession(): boolean {
  return Boolean(getPlatformAccessToken());
}

export function setPlatformSession(accessToken: string, refreshToken: string, admin?: PlatformAdmin): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  if (admin) localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
}

/** Update just the tokens (refresh rotation) without touching the cached admin. */
export function setPlatformTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearPlatformSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ADMIN_KEY);
}
